// src/pages/BibliographyManagementPage.tsx
// 書誌管理画面（Web版）。モバイル版 BibliographyManagementScreen と同じ要件:
// - バーコード（EAN-13/ISBN）をカメラで読み取って本棚に追加
// - タイトル検索で複数選択し、まとめて本棚に追加
//
// バーコード読み取りは「重複を登録しない」（デフォルト、サーバー側で黙ってスキップ）と
// 「重複を確認する」（所有チェック→ダイアログで「登録しない/N+1冊目として登録する」を選択）の
// 2モードをモバイル版と同じ仕様で用意する。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRegistrationStatus } from '../context/RegistrationStatusContext';
import { useAppTokens } from '../context/ThemeModeContext';
import { fetchShelvesRaw, ShelfDetail } from '../utils/shelfHandler';
import {
  searchByIsbn,
  extractRlBookIdFromIsbnResult,
  checkOwnership,
  registerBookByIsbn,
  registerBookByRlBookId,
} from '../utils/bibliographyApi';
import { buildPrefixSearchUrl } from '../config/api';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { PrefixSearchBook } from '../types/rlTypes';
import axios from 'axios';
import BarcodeScannerModal, { ScanResult } from '../components/BarcodeScannerModal';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import SearchIcon from '@mui/icons-material/Search';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Inventory2Icon from '@mui/icons-material/Inventory2';

// MUIの標準アイコンセットには線状バーコードのアイコンが無い（QrCodeScannerはQRコード用で
// 誤解を招くため）ので自作。四隅のスキャン範囲コーナー＋バーコードの縦線で表現する
const LinearBarcodeScanIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props}>
    <path d="M4 4h3v1.6H5.6V7.6H4V4z" />
    <path d="M20 4h-3v1.6h1.4V7.6H20V4z" />
    <path d="M4 20h3v-1.6H5.6v-2H4v3.6z" />
    <path d="M20 20h-3v-1.6h1.4v-2H20v3.6z" />
    <rect x="7.5" y="9" width="1" height="6" />
    <rect x="9.2" y="9" width="0.6" height="6" />
    <rect x="10.4" y="9" width="1.6" height="6" />
    <rect x="12.6" y="9" width="0.6" height="6" />
    <rect x="14" y="9" width="1" height="6" />
    <rect x="15.6" y="9" width="0.6" height="6" />
    <rect x="16.9" y="9" width="1" height="6" />
  </SvgIcon>
);

type View = 'home' | 'results' | 'shelfPicker' | 'confirm';

const NONE_SHELF = '__none__';

const BibliographyManagementPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { idToken, isLoggedIn } = useAuth();
  const { notifyLocalRegister } = useRegistrationStatus();
  const navigate = useNavigate();

  const [view, setView] = useState<View>('home');
  const [shelves, setShelves] = useState<ShelfDetail[]>([]);

  // --- バーコード追加 ---
  const [barcodeShelfId, setBarcodeShelfId] = useState<string>(NONE_SHELF);
  const [ignoreDuplication, setIgnoreDuplication] = useState<boolean>(true);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerPaused, setScannerPaused] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [duplicateDialog, setDuplicateDialog] = useState<{
    isbn: string;
    rlBookId: number;
    count: number;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // --- 検索して追加 ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<PrefixSearchBook[]>([]);
  const [selectedBookIds, setSelectedBookIds] = useState<Set<number>>(new Set());

  // --- 本棚選択（検索追加のバッチ登録用） ---
  const [batchShelfId, setBatchShelfId] = useState<string>(NONE_SHELF);
  const [orderedBooks, setOrderedBooks] = useState<PrefixSearchBook[]>([]);
  const [ownedBookIds, setOwnedBookIds] = useState<Set<number>>(new Set());
  const [registering, setRegistering] = useState(false);

  // 未ログインならトップへ自動遷移。
  // isLoggedIn は Cognito セッション確認が終わるまで一瞬 false になるため
  // （特に新規タブで直接このページを開いた場合）、即座に遷移せず少し待つ。
  useEffect(() => {
    if (isLoggedIn) return;
    const t = window.setTimeout(() => {
      navigate('/', { replace: true });
    }, 1200);
    return () => window.clearTimeout(t);
  }, [isLoggedIn, navigate]);

  const loadShelves = useCallback(async () => {
    if (!idToken) return;
    try {
      const items = await fetchShelvesRaw(idToken);
      items.sort((a, b) => (a.global_shelf_order ?? 0) - (b.global_shelf_order ?? 0));
      setShelves(items);
    } catch (e) {
      // 本棚取得に失敗しても「本棚を選ばない」で続行できる
    }
  }, [idToken]);

  useEffect(() => {
    loadShelves();
  }, [loadShelves]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // ===== バーコード追加 =====
  // 「重複を登録しない」モードでも、常に所有チェックを行ってから登録する。
  // こうしないと「同じ本を何冊もスキャンすると毎回『登録しました』と出るが、
  // 実際にはサーバー側で黙ってスキップされている」という、処理と表示が
  // 食い違う紛らわしい状態になる（モバイル版のダイアログ確認プロセスに相当する
  // 情報を、Web側でも必ずユーザーに見せるようにする）。
  const handleBarcodeDetect = useCallback(
    async (isbn: string) => {
      if (!idToken) return;
      const shelfId = barcodeShelfId === NONE_SHELF ? null : barcodeShelfId;

      setScannerPaused(true);
      setScanResult({ type: 'pending', isbn, message: '書誌情報を確認しています…' });
      try {
        const isbnResult = await searchByIsbn(isbn);
        if (!isbnResult) {
          setScanResult({ type: 'error', isbn, message: '書籍情報の取得に失敗しました。最新刊はデータベース未登録の可能性があります。' });
          setScannerPaused(false);
          return;
        }
        const title: string | undefined = isbnResult?.display?.title;

        const rlBookId = extractRlBookIdFromIsbnResult(isbnResult);
        if (rlBookId == null) {
          setScanResult({ type: 'error', isbn, title, message: '書誌データにrl_book_idが見つかりませんでした' });
          setScannerPaused(false);
          return;
        }

        const ownership = await checkOwnership([rlBookId], idToken);
        const owned = ownership.find((o) => o.rl_book_id === rlBookId);
        const ownedTotal = owned?.total ?? 0;

        if (ownedTotal > 0) {
          if (ignoreDuplication) {
            // 確認せず、重複登録はスキップする（ただし何が起きたかは正直に表示する）
            setScanResult({ type: 'info', isbn, title, message: `既に${ownedTotal}冊登録されているため、重複登録をスキップしました` });
            setScannerPaused(false);
          } else {
            // ダイアログの結果を待つ（スキャンは一時停止したまま。resolveDuplicateDialog側で解除する）
            setScanResult({ type: 'info', isbn, title, message: `既に${ownedTotal}冊登録されています。確認してください。` });
            setDuplicateDialog({ isbn, rlBookId, count: ownedTotal });
          }
          return;
        }

        await registerBookByIsbn(isbn, shelfId, idToken, false);
        notifyLocalRegister();
        setScanResult({ type: 'success', isbn, title, message: '本棚に登録しました' });
        setScannerPaused(false);
      } catch (e) {
        setScanResult({ type: 'error', isbn, message: '登録に失敗しました' });
        setScannerPaused(false);
      }
    },
    [idToken, barcodeShelfId, ignoreDuplication, notifyLocalRegister],
  );

  const resolveDuplicateDialog = async (register: boolean) => {
    if (!duplicateDialog || !idToken) {
      setDuplicateDialog(null);
      setScannerPaused(false);
      return;
    }
    const shelfId = barcodeShelfId === NONE_SHELF ? null : barcodeShelfId;
    if (register) {
      try {
        await registerBookByRlBookId(duplicateDialog.rlBookId, shelfId, idToken, true);
        notifyLocalRegister();
        setScanResult({ type: 'success', isbn: duplicateDialog.isbn, message: `${duplicateDialog.count + 1}冊目として登録しました` });
      } catch (e) {
        setScanResult({ type: 'error', isbn: duplicateDialog.isbn, message: '登録に失敗しました' });
      }
    } else {
      setScanResult({ type: 'info', isbn: duplicateDialog.isbn, message: '登録しませんでした' });
    }
    setDuplicateDialog(null);
    setScannerPaused(false);
  };

  // ===== 検索して追加 =====
  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearchLoading(true);
    setSelectedBookIds(new Set());
    try {
      const res = await axios.get(buildPrefixSearchUrl(q, 50, 0));
      setSearchResults(res.data?.books ?? []);
      setView('results');
    } catch (e) {
      showSnackbar('検索でエラーが発生しました。再度試してください。', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  const toggleBookSelected = (rlBookId: number) => {
    setSelectedBookIds((prev) => {
      const next = new Set(prev);
      if (next.has(rlBookId)) next.delete(rlBookId);
      else next.add(rlBookId);
      return next;
    });
  };

  const goToShelfPicker = async () => {
    const selected = searchResults.filter((b) => selectedBookIds.has(b.rl_book_id));
    setOrderedBooks(selected);
    setBatchShelfId(NONE_SHELF);
    setView('shelfPicker');

    if (idToken && selected.length > 0) {
      try {
        const ownership = await checkOwnership(selected.map((b) => b.rl_book_id), idToken);
        setOwnedBookIds(new Set(ownership.filter((o) => o.total > 0).map((o) => o.rl_book_id)));
      } catch (e) {
        setOwnedBookIds(new Set());
      }
    } else {
      setOwnedBookIds(new Set());
    }
  };

  const moveOrderedBook = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= orderedBooks.length) return;
    const next = [...orderedBooks];
    [next[index], next[target]] = [next[target], next[index]];
    setOrderedBooks(next);
  };

  const handleBatchRegister = async () => {
    if (!idToken || orderedBooks.length === 0) return;
    setRegistering(true);
    const shelfId = batchShelfId === NONE_SHELF ? null : batchShelfId;
    try {
      for (const book of orderedBooks) {
        await registerBookByRlBookId(book.rl_book_id, shelfId, idToken, true);
      }
      notifyLocalRegister(orderedBooks.length);
      setView('confirm');
    } catch (e: any) {
      showSnackbar(`本の登録に失敗しました: ${e?.message ?? e}`, 'error');
    } finally {
      setRegistering(false);
    }
  };

  const resetToHome = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedBookIds(new Set());
    setOrderedBooks([]);
    setOwnedBookIds(new Set());
    setView('home');
  };

  const shelfOptions = useMemo(
    () => shelves.map((s) => ({ id: s.rl_global_shelf_id, name: s.global_shelf_name })),
    [shelves],
  );

  const cardSx = {
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.glass,
    borderRadius: '18px',
    p: 2.5,
  } as const;

  const selectSx = {
    '& .MuiOutlinedInput-root': { color: TOKENS.text, background: TOKENS.buttonBg, borderRadius: '12px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
    '& .MuiSvgIcon-root': { color: TOKENS.textSub },
    '& .MuiInputLabel-root': { color: TOKENS.textSub },
    '& .MuiInputLabel-root.Mui-focused': { color: TOKENS.text },
  } as const;

  return (
    <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg, pb: 6 }}>
      <Box sx={{ maxWidth: 720, margin: 'auto', px: 2, py: { xs: 3, md: 4 } }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: TOKENS.text, mb: 2.5 }}>書誌管理</Typography>

        {view === 'home' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* バーコードで追加 */}
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LinearBarcodeScanIcon sx={{ color: TOKENS.textSub, fontSize: 20 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>バーコードで追加</Typography>
              </Box>

              <FormControl fullWidth size="small" sx={{ ...selectSx, mb: 1.5 }}>
                <InputLabel>追加先の本棚</InputLabel>
                <Select
                  label="追加先の本棚"
                  value={barcodeShelfId}
                  onChange={(e) => setBarcodeShelfId(e.target.value as string)}
                >
                  <MenuItem value={NONE_SHELF}>本棚を選ばない</MenuItem>
                  {shelfOptions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                sx={{ mb: 1.5, color: TOKENS.textSub }}
                control={
                  <Switch
                    checked={!ignoreDuplication}
                    onChange={(e) => setIgnoreDuplication(!e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: TOKENS.text },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: TOKENS.text },
                      '& .MuiSwitch-track': { backgroundColor: TOKENS.borderSoft },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                    {ignoreDuplication ? '重複を登録しない' : '重複を確認する'}
                  </Typography>
                }
              />

              <Button
                fullWidth
                onClick={() => {
                  setScanResult(null);
                  setScannerOpen(true);
                }}
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1,
                  color: TOKENS.bg,
                  background: TOKENS.text,
                  '&:hover': { background: TOKENS.textSub },
                }}
              >
                バーコードリーダーを起動
              </Button>
            </Box>

            {/* 検索して追加 */}
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <SearchIcon sx={{ color: TOKENS.textSub, fontSize: 20 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>検索して追加</Typography>
              </Box>

              <TextField
                fullWidth
                size="small"
                label="書籍検索"
                placeholder="書籍のタイトルや著者名を入力"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                sx={{ ...selectSx, mb: 1 }}
              />
              <Typography sx={{ fontSize: 11, color: TOKENS.textSub, mb: 1.5 }}>
                ※追加する本棚は検索結果から本を選択した後に選択できます
              </Typography>

              <Button
                fullWidth
                disabled={!searchQuery.trim() || searchLoading}
                onClick={handleSearch}
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1,
                  color: TOKENS.bg,
                  background: TOKENS.text,
                  '&:hover': { background: TOKENS.textSub },
                  '&.Mui-disabled': { color: TOKENS.bg, background: TOKENS.borderSoft },
                }}
              >
                {searchLoading ? <CircularProgress size={18} sx={{ color: TOKENS.bg }} /> : '検索'}
              </Button>
            </Box>

            {/* インポート */}
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FileUploadIcon sx={{ color: TOKENS.textSub, fontSize: 20 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>インポート</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1.5 }}>
                他の読書管理サービスからのデータをまとめて取り込みます
              </Typography>
              <Button
                component={Link}
                to="/importbooks"
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 700,
                  px: 2,
                  py: 0.75,
                  color: TOKENS.text,
                  background: TOKENS.buttonBg,
                  border: `1px solid ${TOKENS.borderSoft}`,
                  '&:hover': { borderColor: TOKENS.accent, background: TOKENS.buttonBgHover },
                }}
              >
                インポートページへ
              </Button>
            </Box>

            {/* エクスポート */}
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FileDownloadIcon sx={{ color: TOKENS.textSub, fontSize: 20 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>エクスポート</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1.5 }}>
                登録した本・抜粋メモをCSVファイルで書き出します
              </Typography>
              <Button
                component={Link}
                to="/exportbooks"
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 700,
                  px: 2,
                  py: 0.75,
                  color: TOKENS.text,
                  background: TOKENS.buttonBg,
                  border: `1px solid ${TOKENS.borderSoft}`,
                  '&:hover': { borderColor: TOKENS.accent, background: TOKENS.buttonBgHover },
                }}
              >
                エクスポートページへ
              </Button>
            </Box>

            {/* シール管理 */}
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocalOfferIcon sx={{ color: TOKENS.textSub, fontSize: 20 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>シール管理</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1.5 }}>
                登録した本のQRコード付きシールをPDFで作成します
              </Typography>
              <Button
                component={Link}
                to="/stickers"
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 700,
                  px: 2,
                  py: 0.75,
                  color: TOKENS.text,
                  background: TOKENS.buttonBg,
                  border: `1px solid ${TOKENS.borderSoft}`,
                  '&:hover': { borderColor: TOKENS.accent, background: TOKENS.buttonBgHover },
                }}
              >
                シール管理ページへ
              </Button>
            </Box>

            {/* 除籍した本の管理 */}
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Inventory2Icon sx={{ color: TOKENS.textSub, fontSize: 20 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>除籍した本の管理</Typography>
              </Box>
              <Button
                component={Link}
                to="/withdrawn-books"
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 700,
                  px: 2,
                  py: 0.75,
                  color: TOKENS.text,
                  background: TOKENS.buttonBg,
                  border: `1px solid ${TOKENS.borderSoft}`,
                  '&:hover': { borderColor: TOKENS.accent, background: TOKENS.buttonBgHover },
                }}
              >
                除籍済みの本
              </Button>
            </Box>
          </Box>
        )}

        {view === 'results' && (
          <Box>
            <Button
              onClick={() => setView('home')}
              sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}
            >
              ← 戻る
            </Button>

            {searchResults.length === 0 ? (
              <Typography sx={{ color: TOKENS.textSub, fontSize: 13, textAlign: 'center', mt: 4 }}>
                検索結果が0件です。
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pb: 10 }}>
                {searchResults.map((book) => {
                  const checked = selectedBookIds.has(book.rl_book_id);
                  return (
                    <Box
                      key={book.rl_book_id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        border: `1px solid ${TOKENS.border}`,
                        background: TOKENS.glass,
                        borderRadius: '14px',
                        p: 1.25,
                      }}
                    >
                      <Checkbox checked={checked} onChange={() => toggleBookSelected(book.rl_book_id)} />
                      <Box
                        component="img"
                        src={generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13)}
                        alt={book.title}
                        sx={{ width: 40, height: 56, objectFit: 'contain', borderRadius: '4px', background: TOKENS.cardBg, flexShrink: 0 }}
                      />
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography
                          component="a"
                          href={`/book/${book.rl_book_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: 13,
                            color: TOKENS.text,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {book.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub, mt: 0.25 }} noWrap>
                          {[book.responsibility, book.publisher, book.publishing_date].filter(Boolean).join(' / ')}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                background: TOKENS.glassStrong,
                borderTop: `1px solid ${TOKENS.border}`,
                p: 2,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                disabled={selectedBookIds.size === 0}
                onClick={goToShelfPicker}
                sx={{
                  width: '100%',
                  maxWidth: 720,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1.1,
                  color: TOKENS.bg,
                  background: TOKENS.text,
                  '&:hover': { background: TOKENS.textSub },
                  '&.Mui-disabled': { color: TOKENS.bg, background: TOKENS.borderSoft },
                }}
              >
                選択した本を本棚に登録する（{selectedBookIds.size}冊）
              </Button>
            </Box>
          </Box>
        )}

        {view === 'shelfPicker' && (
          <Box>
            <Button
              onClick={() => setView('results')}
              disabled={registering}
              sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}
            >
              ← 戻る
            </Button>

            <FormControl fullWidth size="small" sx={{ ...selectSx, mb: 1.5 }}>
              <InputLabel>追加先の本棚</InputLabel>
              <Select
                label="追加先の本棚"
                value={batchShelfId}
                onChange={(e) => setBatchShelfId(e.target.value as string)}
              >
                <MenuItem value={NONE_SHELF}>本棚を選ばない</MenuItem>
                {shelfOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography sx={{ fontSize: 11, color: TOKENS.textSub, mb: 1.5 }}>
              ↑↓で並び替えができ、表示順で本棚に登録されます。
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pb: 10 }}>
              {orderedBooks.map((book, index) => (
                <Box
                  key={book.rl_book_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    border: `1px solid ${TOKENS.border}`,
                    background: TOKENS.glass,
                    borderRadius: '14px',
                    p: 1.25,
                  }}
                >
                  <Box
                    component="img"
                    src={generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13)}
                    alt={book.title}
                    sx={{ width: 36, height: 50, objectFit: 'contain', borderRadius: '4px', background: TOKENS.cardBg, flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography sx={{ fontSize: 13, color: TOKENS.text }} noWrap>
                      {book.title}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }} noWrap>
                      {book.responsibility}
                    </Typography>
                    {ownedBookIds.has(book.rl_book_id) && (
                      <Typography sx={{ fontSize: 11, color: '#e0a000', mt: 0.25 }}>
                        すでに本棚に登録されています
                      </Typography>
                    )}
                  </Box>
                  <IconButton size="small" disabled={index === 0} onClick={() => moveOrderedBook(index, -1)}>
                    <ArrowUpwardIcon sx={{ fontSize: 16, color: index === 0 ? TOKENS.borderSoft : TOKENS.textSub }} />
                  </IconButton>
                  <IconButton size="small" disabled={index === orderedBooks.length - 1} onClick={() => moveOrderedBook(index, 1)}>
                    <ArrowDownwardIcon
                      sx={{ fontSize: 16, color: index === orderedBooks.length - 1 ? TOKENS.borderSoft : TOKENS.textSub }}
                    />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                background: TOKENS.glassStrong,
                borderTop: `1px solid ${TOKENS.border}`,
                p: 2,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                disabled={orderedBooks.length === 0 || registering}
                onClick={handleBatchRegister}
                sx={{
                  width: '100%',
                  maxWidth: 720,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1.1,
                  color: TOKENS.bg,
                  background: TOKENS.text,
                  '&:hover': { background: TOKENS.textSub },
                  '&.Mui-disabled': { color: TOKENS.bg, background: TOKENS.borderSoft },
                }}
              >
                {registering ? <CircularProgress size={18} sx={{ color: TOKENS.bg }} /> : '選択した本を本棚に登録する'}
              </Button>
            </Box>
          </Box>
        )}

        {view === 'confirm' && (
          <Box sx={{ ...cardSx, textAlign: 'center', mt: 4 }}>
            <Typography sx={{ fontSize: 15, color: TOKENS.text, mb: 2.5 }}>書籍の登録が完了しました。</Typography>
            <Button
              onClick={resetToHome}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                py: 1,
                color: TOKENS.bg,
                background: TOKENS.text,
                '&:hover': { background: TOKENS.textSub },
              }}
            >
              書誌管理画面に戻る
            </Button>
          </Box>
        )}
      </Box>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetect={handleBarcodeDetect}
        paused={scannerPaused}
        lastResult={scanResult}
      />

      {/* 重複確認ダイアログ */}
      <Dialog open={duplicateDialog !== null} disableEscapeKeyDown>
        <DialogTitle>重複の確認</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            この本は既に{duplicateDialog?.count}冊登録されています。どうしますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resolveDuplicateDialog(false)} sx={{ color: TOKENS.textSub, textTransform: 'none' }}>
            登録しない
          </Button>
          <Button
            onClick={() => resolveDuplicateDialog(true)}
            variant="contained"
            disableElevation
            sx={{
              textTransform: 'none',
              color: TOKENS.bg,
              background: TOKENS.text,
              '&:hover': { background: TOKENS.textSub },
            }}
          >
            {(duplicateDialog?.count ?? 0) + 1}冊目として登録する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BibliographyManagementPage;
